// Полетата в отметнат ред на „Целия списък": количество (+ мярката до него) и
// при доставка ед. цена нето + сумата на реда. Неотметнатият ред показва на
// мястото на количеството приглушено стойността си в листа, ако има.
//
// Мишката: кликът по поле НЕ отмята/маха реда и НЕ връща фокуса във филтъра
// (редът спира mousedown на десктоп) — затова и двете спират тук.
// Клавишите (Enter → следващият ред, Ctrl+Enter, Esc) са в useCatalogPicker.
import { formatMoney } from "../numbers";
import {
  catalogFieldId,
  entryLineSumOf,
  entryPriceOf,
  type CatalogEntryField,
  type CatalogEntryValue,
} from "./catalogEntryModel";
import type { CatalogItem } from "./catalogPickerModel";

export type CatalogFieldHandlers = {
  onFieldChange: (id: number, field: CatalogEntryField, value: string) => void;
  onFieldKeyDown: (index: number, event: React.KeyboardEvent<HTMLInputElement>) => void;
  // фокус в поле → маркерът застава на реда
  onFieldFocus: (index: number) => void;
};

type FieldProps = CatalogFieldHandlers & {
  item: CatalogItem;
  index: number;
  optionId: string;
  value: CatalogEntryValue | undefined;
  isNarrow: boolean;
  label: string;
};

// Плоско поле — малкият контрол на общия стандарт: 24px в реда от 34px (36px с
// пръст; 38px и 16px шрифт на телефон — иначе iOS зуумва), кехлибарен фокус
const fieldClass = (isNarrow: boolean) =>
  `box-border rounded-md border border-shade-3 bg-surface px-1.5 text-right font-sheet tabular-nums text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
    isNarrow ? "h-[38px] text-base" : "h-6 text-[13px] leading-[normal] pointer-coarse:h-9"
  }`;

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

const EntryInput = ({
  item,
  index,
  optionId,
  field,
  value,
  placeholder,
  className,
  label,
  onFieldChange,
  onFieldKeyDown,
  onFieldFocus,
}: CatalogFieldHandlers & {
  item: CatalogItem;
  index: number;
  optionId: string;
  field: CatalogEntryField;
  value: string;
  placeholder?: string;
  className: string;
  label: string;
}) => (
  <input
    id={catalogFieldId(optionId, field)}
    value={value}
    placeholder={placeholder}
    inputMode="decimal"
    autoComplete="off"
    aria-label={`${label} — ${item.name}`}
    onChange={(event) => onFieldChange(item.id, field, event.target.value)}
    onKeyDown={(event) => onFieldKeyDown(index, event)}
    onFocus={() => onFieldFocus(index)}
    onMouseDown={stop}
    onClick={stop}
    className={className}
  />
);

// Количеството: поле + мярката (базовата, разфасовката на реда, „порц.")
export const CatalogQtyField = ({ value, isNarrow, ...props }: FieldProps) => (
  <>
    <EntryInput
      {...props}
      field="qty"
      value={value?.qty ?? ""}
      // стойността на реда в листа — видна, докато полето е празно
      placeholder={props.item.filledValue}
      className={`${fieldClass(isNarrow)} ${isNarrow ? "w-[72px]" : "w-[84px]"}`}
    />
    {props.item.entryUnit && (
      <span className={`min-w-0 truncate text-muted ${isNarrow ? "max-w-[34px] text-[12px]" : "text-[12px]"}`}>
        {props.item.entryUnit}
      </span>
    )}
  </>
);

// Ед. цената: попълнена с предложената (сива, курсив — като автоматичната
// цена на листа), докато операторът не я пипне
export const CatalogPriceField = ({ value, isNarrow, ...props }: FieldProps) => {
  const untouched = value?.price === undefined;
  return (
    <EntryInput
      {...props}
      field="price"
      value={entryPriceOf(props.item, value)}
      className={`${fieldClass(isNarrow)} ${isNarrow ? "w-[64px]" : "w-full"} ${
        untouched ? "italic text-soft focus:not-italic focus:text-ink" : ""
      }`}
    />
  );
};

// Сумата нето на реда (к-во × ед. цена) — празно, докато няма и двете
export const CatalogLineSum = ({ item, value }: { item: CatalogItem; value: CatalogEntryValue | undefined }) => {
  const sum = entryLineSumOf(item, value);
  return sum == null ? null : <span className="font-sheet tabular-nums text-ink">{formatMoney(sum)}</span>;
};
